import React, { useState } from "react";
import { Drawer, Typography, Tag, Divider } from "antd";
import {
  CloseCircleOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  TagOutlined,
  ExpandOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;

const statusMap = {
  DRAFT: { label: "Draft", color: "#64748b" }, // slate
  ACTIVE: { label: "Active", color: "#10b981" }, // emerald
  CURRENT: { label: "Current", color: "#3b82f6" }, // blue
  EXPIRED: { label: "Expired", color: "#f59e0b" }, // amber
  DISABLED: { label: "Disabled", color: "#ef4444" }, // red
};

const MetaRow = ({ icon, label, value }) => (
  <div style={{ display: "flex", marginBottom: 16 }}>
    <div
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: "#6b7280",
        fontSize: 13,
      }}
    >
      {icon}
      <span>{label}</span>
    </div>
    <div style={{ flex: 2 }}>{value || <Text type="secondary">—</Text>}</div>
  </div>
);

const AppVersionDrawer = ({ visible, appVersion, onClose }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!appVersion) return null;

  const status = statusMap[appVersion.status];

  return (
    <Drawer
      open={visible}
      onClose={onClose}
      placement="right"
      size={isExpanded ? 520 : "100vw"}
      zIndex={99999}
      closable={false}
      destroyOnHidden
      styles={{ padding: 24 }}
    >
      <div
        style={{
          maxWidth: isExpanded ? "100vw" : 700,
          margin: isExpanded ? 0 : "0 auto",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {/* header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={4} style={{ marginBottom: 4 }}>
              {appVersion.versionName}
            </Title>
            <Text type="secondary">{appVersion.appName}</Text>
          </div>

          <div style={{ display: "flex", gap: 16 }}>
            <ExpandOutlined
              className="task-drawer-icon"
              onClick={() => setIsExpanded((prev) => !prev)}
              style={{ cursor: "pointer" }}
            />
            <CloseCircleOutlined
              onClick={onClose}
              style={{ fontSize: 18, cursor: "pointer", color: "#9ca3af" }}
            />
          </div>
        </div>

        {/* status tags */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 24,
          }}
        >
          {status && (
            <Tag
              style={{
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 500,
                backgroundColor: status.color,
                border: "none",
                color: "#fff",
                fontFamily: "Inter",
              }}
            >
              {status.label}
            </Tag>
          )}

          {appVersion.forceUpdate && (
            <Tag
              style={{
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 500,
                backgroundColor: "#fee2e2",
                color: "#b91c1c",
                border: "none",
                fontFamily: "Inter",
              }}
            >
              Force Update
            </Tag>
          )}

          {appVersion.isMinimumSupported && (
            <Tag
              style={{
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 500,
                backgroundColor: "#e5e7eb",
                color: "#374151",
                border: "none",
                fontFamily: "Inter",
              }}
            >
              Minimum Supported
            </Tag>
          )}

          {appVersion.isRecommended && (
            <Tag
              style={{
                borderRadius: 20,
                padding: "4px 12px",
                fontWeight: 500,
                backgroundColor: "#dcfce7",
                color: "#166534",
                border: "none",
                fontFamily: "Inter",
              }}
            >
              Recommended
            </Tag>
          )}
        </div>

        <Divider />

        {/* version info */}
        <MetaRow
          icon={<InfoCircleOutlined />}
          label="Version Code (Android)"
          value={<Text>{appVersion.versionCode}</Text>}
        />

        <MetaRow
          icon={<InfoCircleOutlined />}
          label="Build Number (iOS)"
          value={<Text>{appVersion.buildNumber}</Text>}
        />

        <MetaRow
          icon={<CalendarOutlined />}
          label="Effective From"
          value={
            <Text>
              {appVersion.effectiveFrom
                ? dayjs(appVersion.effectiveFrom).format("MMM D, YYYY HH:mm")
                : "—"}
            </Text>
          }
        />

        <Divider />

        {/* update config */}
        <MetaRow
          icon={<TagOutlined />}
          label="Update Title"
          value={<Text>{appVersion.updateTitle}</Text>}
        />

        {appVersion.updateMessage && (
          <div style={{ marginBottom: 24 }}>
            <Text
              type="secondary"
              style={{ fontSize: 13, display: "block", marginBottom: 6 }}
            >
              Update Message
            </Text>

            <div
              style={{
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                padding: 12,
                background: "#fafafa",
              }}
            >
              <Text>{appVersion.updateMessage}</Text>
            </div>
          </div>
        )}

        <Divider />

        {/* store links */}
        {appVersion.androidStoreUrl && (
          <MetaRow
            icon={<LinkOutlined />}
            label="Google Play"
            value={
              <a
                href={appVersion.androidStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {appVersion.androidStoreUrl}
              </a>
            }
          />
        )}

        {appVersion.appleStoreUrl && (
          <MetaRow
            icon={<LinkOutlined />}
            label="Apple Store"
            value={
              <a
                href={appVersion.appleStoreUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {appVersion.appleStoreUrl}
              </a>
            }
          />
        )}
      </div>
    </Drawer>
  );
};

export default AppVersionDrawer;
